import { z as zod } from "zod";
import { ExtendRoomProvider } from "@onair/constants";
import { schemaHelper } from "@onair/minimal-ui/hook-form";
import dayjs from "@onair/utils/dayjs";
import {
	TicketTierOfflineSchema,
	TicketTierOnlineSchema,
} from "./ticket-tier-schema";
import { LocationType } from "@onair/repositories";
import { SocialCommunitiesSchema } from "./social-communities-schema";

export const StepTimelineAndPricingSchema = zod
	.object({
		locationType: zod.nativeEnum(LocationType),
		startDate: schemaHelper
			.date({
				message: {
					required_error: "Vui lòng nhập thời gian bắt đầu của sự kiện",
				},
			})
			.refine((data) => dayjs().isBefore(data), {
				message: "Thời gian bắt đầu phải lớn hơn hiện tại",
			}),
		endDate: schemaHelper
			.date({
				message: {
					required_error: "Vui lòng nhập thời gian kết thúc của sự kiện",
				},
			})
			.refine((data) => dayjs().isBefore(data), {
				message: "Thời gian kết thúc phải lớn hơn thời gian hiện tại.",
			}),
		roomProvider: zod
			.nativeEnum(ExtendRoomProvider, {
				required_error: "Vui lòng chọn nền tảng tổ chức sự kiện",
				invalid_type_error: "Vui lòng chọn nền tảng tổ chức sự kiện",
			})
			.optional(),
		roomUrl: zod
			.string({
				required_error: "Vui lòng nhập link tham gia sự kiện",
			})
			.url({
				message: "Vui lòng nhập link tham gia sự kiện hợp lệ",
			})
			.optional(),
		roomInfo: zod
			.object({
				id: zod.string().optional(),
				password: zod.string().optional(),
			})
			.optional(),
		address: zod.string().optional(),
		socialCommunities: zod.array(SocialCommunitiesSchema),
		ticketTiers: zod.array(zod.any()).min(1, {
			message: "Vui lòng thêm ít nhất một loại vé",
		}),
	})
	.refine(
		(data) => {
			return (
				!data.endDate ||
				!data.startDate ||
				dayjs(data.endDate).isAfter(data.startDate)
			);
		},
		{
			message: "Ngày kết thúc phải lớn hơn ngày bắt đầu",
			path: ["endDate"],
		},
	)
	.refine(
		(data) => {
			return (
				data.roomProvider === ExtendRoomProvider.SYSTEM ||
				data.locationType === LocationType.OFFLINE ||
				!!data.roomUrl
			);
		},
		{
			message: "Vui lòng nhập link tham gia sự kiện",
			path: ["roomUrl"],
		},
	)
	.refine(
		(data) => {
			return (
				data.locationType !== LocationType.ONLINE ||
				data.roomProvider !== undefined
			);
		},
		{
			message: "Vui lòng chọn nền tảng tổ chức sự kiện",
			path: ["roomProvider"],
		},
	)
	.refine(
		(data) => {
			return (
				data.locationType !== LocationType.OFFLINE || !!data.address?.trim()
			);
		},
		{
			message: "Vui lòng nhập địa điểm tổ chức sự kiện",
			path: ["address"],
		},
	)
	.refine(
		(data) => {
			return (
				!data.socialCommunities.some((item) => item.socialCommunityLink) ||
				!!data.socialCommunities.some((item) => item.socialCommunityName)
			);
		},
		{
			message: "Vui lòng nhập tên nhóm cộng đồng",
			path: ["socialCommunityName"],
		},
	)
	.superRefine((data, ctx) => {
		data.ticketTiers.forEach((ticketTier, ticketTierIndex) => {
			try {
				(data?.locationType === LocationType.OFFLINE
					? TicketTierOfflineSchema
					: TicketTierOnlineSchema
				).parse(ticketTier);
			} catch (e) {
				if (e instanceof zod.ZodError) {
					const issues = e.issues;

					issues.forEach((issue) => {
						ctx.addIssue({
							code: zod.ZodIssueCode.custom,
							message: issue.message,
							path: ["ticketTiers", ticketTierIndex, ...issue.path],
						});
					});
				}
			}
		});
	});
